import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { pathOr } from 'ramda'
import React from 'react'
import SMSHelper from 'smshelper'

import Select from '@/app/component/atom/form/select'
import IconText from '@/app/component/layout/icon-text'
import Message from '@/app/component/send/message'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import { DEFAULT_SENDER_ID_MESSAGE, DEFAULT_SENDER_ID_VALUE } from '@/app/definitions'
import SMSLanguageWrapper from '@/app/module/campaigns/component/item/steps/content/language-wrapper'
import { getSMSLanguageMap } from '@/app/module/campaigns/component/item/steps/content/types/multimessage/content'
import { displayMap, smsMaxLength } from '@/app/module/campaigns/definitions'
import { hasContent, LanguageType } from '@/app/module/campaigns/helpers'
import { LocalSendSMSAction } from '@/app/module/campaigns/types'
import { PhoneNumber } from '@/app/module/phonenumbers/types'
import { decodeText } from '@/app/module/utils/text'
import { PersonalizationType, SelectOption } from '@/app/types'

import { getSmsSenderIds } from './sms-sender-ids'

type Props = {
  defaultLanguage: string
  defaultSenderId?: SelectOption<string>
  isNewAction: boolean
  languages: LanguageType[]
  phoneNumber?: PhoneNumber
  senderIds: SelectOption<string>[]
  personalizationList: PersonalizationType
  value: LocalSendSMSAction
  onClose: () => void
  onSave: (value: Partial<LocalSendSMSAction>) => void
}

type ContentSMS = Pick<LocalSendSMSAction['sendSMS'], 'text' | 'translations'>

const SendSMS = (props: Props) => {
  const { text, senderId: initialSenderId, useCampaignSenderId, translations } = props.value.sendSMS
  const [message, setMessage] = React.useState<ContentSMS>({
    text,
    translations,
  })
  const [senderId, setSenderId] = React.useState(initialSenderId)
  const senderIds = React.useMemo(
    () => getSmsSenderIds(props.defaultSenderId, props.senderIds),
    [props.defaultSenderId, props.senderIds],
  )

  React.useEffect(() => {
    if (props.defaultSenderId && useCampaignSenderId !== 'action') {
      setSenderId(props.defaultSenderId.value)
    } else if (props.isNewAction) {
      // if there's no default sender id, and it's a new action, we need to set a default sender id
      const pn = props.phoneNumber
      setSenderId(pn && pn.sms ? pn.phoneNumber : DEFAULT_SENDER_ID_VALUE)
    } else {
      setSenderId(initialSenderId === '' ? DEFAULT_SENDER_ID_VALUE : initialSenderId)
    }
  }, [initialSenderId, props.defaultSenderId, props.isNewAction, props.phoneNumber, useCampaignSenderId])

  const { personalizationList, onSave, onClose, defaultSenderId } = props

  const parts = SMSHelper.parts(message.text)

  const changeHandler = ({ message: updatedMessage }: { message: ContentSMS }) => {
    setMessage(updatedMessage)
  }

  const languageContentMap = React.useMemo(
    () => getSMSLanguageMap(message, props.defaultLanguage),
    [message, props.defaultLanguage],
  )

  return (
    <CustomDialog open={true} onClose={onClose}>
      <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <IconText>
            <Icon>{displayMap.sms.icon}</Icon>
            <Typography variant="h6">Send SMS to contact</Typography>
          </IconText>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            width: '70vw',
            minWidth: '300px',
            maxWidth: '100%',
          }}
        >
          <SMSLanguageWrapper
            content="text"
            defaultLanguage={props.defaultLanguage}
            item={{ message }}
            info="Add your message below"
            languageContentMap={languageContentMap}
            languages={props.languages}
            type="message"
            onChange={changeHandler}
          >
            {(activeText: string, onTextChange: (text: string) => void, activeError?: string) => (
              <Message
                autoFocus
                message={activeText}
                error={activeError}
                personalizationList={personalizationList}
                onChange={onTextChange}
              />
            )}
          </SMSLanguageWrapper>
          <div
            style={{
              marginTop: '10px',
            }}
          >
            <Select
              id="send-message-sender"
              label="Sender ID"
              name="senderId"
              value={senderId}
              values={senderIds}
              editable={true}
              className="send-message-sender"
              onChange={({ value }) => {
                setSenderId(value)
              }}
              info={
                senderId === DEFAULT_SENDER_ID_VALUE && senderIds.length === 1
                  ? DEFAULT_SENDER_ID_MESSAGE
                  : 'Select what should appear on the recipients phones as whom this SMS Message is from (e.g., MyOrg or 12125551212)'
              }
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          id="send-sms-action-save"
          disabled={!hasContent(message, 'text') || senderId === '0' || senderId === undefined || parts > smsMaxLength}
          onClick={() =>
            onSave({
              sendSMS: {
                text: decodeText(message.text),
                translations: message.translations,
                senderId:
                  senderId === DEFAULT_SENDER_ID_VALUE || senderId === pathOr('', ['value'], defaultSenderId)
                    ? ''
                    : senderId,
                useCampaignSenderId:
                  defaultSenderId && senderId === pathOr('', ['value'], defaultSenderId)
                    ? defaultSenderId.value
                    : 'action',
              },
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </CustomDialog>
  )
}

export default SendSMS
